import React, { useContext, useEffect, useLayoutEffect, useState } from "react"
import { Close } from "@/components/svg/Close"
import SignUpForm from "@/components/SignUpForm"
import {
  buildRequestBodyForExternalAuth,
  buildRequestBodyForNormalAuth,
  createSession,
  fetchUrlParams,
} from "@/utils/signup"
import { appServerHost } from "@/utils/app-server"
import ExternalSignUpButton from "@/components/ExternalSignUpButton"
import { MicrosoftLogo } from "@/components/svg/MicrosoftLogo"
import { GoogleLogo } from "@/components/svg/GoogleLogo"
import {
  I18nextContext,
  useTranslation,
} from "@zestia/gatsby-plugin-react-i18next"
import { usePreventScroll } from "react-aria"
import { Spinner } from "@/components/svg/Spinner"
import { graphql, useStaticQuery } from "gatsby"
import G2StarsOrange from "../svg/G2StarsOrange"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import findImage from "@/utils/find-image"

const SignUpModal = ({
  location,
  tier,
  variation,
  title,
  closeModal,
  frequency,
}) => {
  usePreventScroll()
  const { language } = useContext(I18nextContext)
  const { t } = useTranslation()
  const effectiveLocation =
    location || (typeof window !== "undefined" ? window.location : undefined)

  const [isLoadingMicrosoftSignup, setIsLoadingMicrosoftSignup] =
    useState(false)
  const [isLoadingGoogleSignup, setIsLoadingGoogleSignup] = useState(false)
  const [isLoadingNormalSignup, setIsLoadingNormalSignup] = useState(false)

  const [isLoading, setIsLoading] = useState(false)

  const [error, setError] = useState(null)

  useEffect(() => {
    setIsLoading(
      isLoadingNormalSignup ||
        isLoadingMicrosoftSignup ||
        isLoadingGoogleSignup,
    )
  }, [isLoadingNormalSignup, isLoadingMicrosoftSignup, isLoadingGoogleSignup])

  useEffect(() => {
    if (!variation && effectiveLocation) {
      variation = fetchUrlParams(effectiveLocation).get("variation")
    }
  }, [effectiveLocation])

  // Clear hash from URL when modal opens to prevent scroll conflicts
  // Use useLayoutEffect to run synchronously before paint
  useLayoutEffect(() => {
    if (tier && window.location.hash) {
      // Store current scroll position
      const scrollY = window.scrollY || window.pageYOffset

      // Clear hash immediately
      window.history.replaceState(null, "", window.location.pathname)

      // Restore scroll position to prevent unwanted scrolling
      window.scrollTo(0, scrollY)
    }
  }, [tier])

  const handleMicrosoftSignup = () => {
    setError(null)
    setIsLoadingMicrosoftSignup(true)

    createSession(
      effectiveLocation,
      buildRequestBodyForExternalAuth(
        "Outlook",
        tier,
        variation,
        language,
        frequency,
      ),
    )
      .then(data => {
        let signup = data.signup
        let sessionId = signup ? signup.id : null
        if (sessionId) {
          window.location =
            "https://app." +
            appServerHost(effectiveLocation) +
            "/oauth2/signup/microsoft?session_id=" +
            sessionId
        } else {
          throw new Error("Unable to process your request")
        }
      })
      .catch(error => {
        setError(error.message)
        setIsLoadingMicrosoftSignup(false)
      })
  }

  const handleGoogleSignup = () => {
    setError(null)
    setIsLoadingGoogleSignup(true)

    createSession(
      effectiveLocation,
      buildRequestBodyForExternalAuth(
        "Google",
        tier,
        variation,
        language,
        frequency,
      ),
    )
      .then(data => {
        let signup = data.signup
        let sessionId = signup ? signup.id : null
        if (sessionId) {
          window.location =
            "https://app." +
            appServerHost(effectiveLocation) +
            "/oauth2/signup/google?session_id=" +
            sessionId
        } else {
          throw new Error("Unable to process your request")
        }
      })
      .catch(error => {
        setError(error.message)
        setIsLoadingGoogleSignup(false)
      })
  }

  const handleNormalSignup = formData => {
    setError(null)

    if (!tier) {
      setError("Plan is required")
      return
    }

    if (!isLoading) {
      setIsLoadingNormalSignup(true)

      createSession(
        effectiveLocation,
        buildRequestBodyForNormalAuth(
          formData.emailAddress,
          tier,
          variation,
          language,
          frequency,
        ),
      )
        .then(data => {
          let signup = data.signup
          let sessionId = signup ? signup.id : null

          if (sessionId) {
            window.location =
              "https://auth." +
              appServerHost(effectiveLocation) +
              "/register/" +
              sessionId
          } else {
            throw new Error("Unable to process your request")
          }
        })
        .catch(error => {
          setError(error.message)
          setIsLoadingNormalSignup(false)
        })
    }
  }

  // Get image via static query
  const data = useStaticQuery(graphql`
    query {
      allFile(
        filter: {
          sourceInstanceName: { eq: "images" }
          relativeDirectory: { eq: "social-proof" }
        }
      ) {
        edges {
          node {
            childImageSharp {
              gatsbyImageData(placeholder: BLURRED, quality: 70)
            }
          }
        }
      }
    }
  `)

  return (
    <div className="signup-modal">
      {/* Backdrop */}
      <button
        className="signup-modal__backdrop"
        onClick={() => closeModal()}
        aria-label={t("signup.close-signup-modal")}
      ></button>

      <div className="container-fluid">
        <div className="signup-modal__box">
          <button
            type="button"
            className="signup-modal__close js-modal__close"
            aria-label={t("signup.close-signup-modal")}
            onClick={() => closeModal()}
          >
            <Close />
          </button>

          {/* Left */}
          <div className="signup-modal__social-proof center-xs">
            <p className="mb-0 p--sm">{t("sales-resources.reviews.one")}</p>

            <G2StarsOrange />

            <GatsbyImage
              image={getImage(findImage(data.allFile, "g2-three-badges"))}
              alt=""
              className="mt-10"
            />

            <hr className="mt-10" />

            <p className="mb-0 mt-10 signup-modal__quote">
              "{t("signup.modal-quote")}"
            </p>
          </div>

          {/* Right */}
          <div className="signup-modal__content">
            <p className="text-bold tag tag--filled p--xs center-xs mb-2">
              {t("common.no-card-required")}
            </p>

            <h2 className="mb-0 text-centered signup-modal__title h4">
              {tier === "free"
                ? t("signup.modal-title.free")
                : t("signup.modal-title.14-day")}
            </h2>

            <p className="mb-8 text-centered signup-modal__subtitle">
              {tier !== "free" && title}
            </p>

            {error && (
              <div className="signup-modal__error-message">
                <p className="p--md">{error}</p>
              </div>
            )}

            <SignUpForm
              isLoading={isLoadingNormalSignup}
              disabled={isLoading}
              onSubmit={handleNormalSignup}
              planName={t(`plans.name.${tier}`)}
            />

            <div className="signup-modal__divider mt-4 mb-4">
              <span className="signup-modal__divider-text">
                {t("signup.signup-options-divider")}
              </span>
            </div>

            <ExternalSignUpButton
              gtm={{
                label: "Continue with Microsoft",
                category: "Signup Button",
              }}
              disabled={isLoading}
              onClick={() => handleMicrosoftSignup()}
              planName={t(`plans.name.${tier}`)}
            >
              {isLoadingMicrosoftSignup ? (
                <Spinner />
              ) : (
                <>
                  <MicrosoftLogo />
                  {t("signup.modal-signup-buttons.microsoft")}
                </>
              )}
            </ExternalSignUpButton>

            <ExternalSignUpButton
              gtm={{
                label: "Continue with Google Workspace",
                category: "Signup Button",
              }}
              disabled={isLoading}
              onClick={() => handleGoogleSignup()}
              className={"mt-4"}
              planName={t(`plans.name.${tier}`)}
            >
              {isLoadingGoogleSignup ? (
                <Spinner />
              ) : (
                <>
                  <GoogleLogo />
                  {t("signup.modal-signup-buttons.google")}
                </>
              )}
            </ExternalSignUpButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignUpModal
